import Reflux from 'reflux';
import BaseMixin from 'flux/stores/BaseMixin';
import { Map, OrderedMap, List, fromJS } from 'immutable';
import GuestPassActions from 'flux/actions/GuestPassActions';
import EventActions from 'flux/actions/EventActions';
import EventViewActions from 'flux/actions/EventViewActions';
import TeamViewActions from 'flux/actions/TeamViewActions';
import GlobalElementActions from 'flux/actions/GlobalElementActions';
import TagEditFormActions from 'flux/actions/TagEditFormActions';
import TagFormActions from 'flux/actions/TagFormActions';
import TagListActions from 'flux/actions/TagListActions';
import CuesheetActions from 'flux/actions/cuesheet/CuesheetActions';
import CuesheetRowActions from 'flux/actions/cuesheet/CuesheetRowActions';
import CuesheetCellActions from 'flux/actions/cuesheet/CuesheetCellActions';
import GlobalElementSetFormActions from 'flux/actions/GlobalElementSetFormActions';
import GlobalElementSetEditFormActions from 'flux/actions/GlobalElementSetEditFormActions';
import NewGlobalElementModalActions from 'flux/actions/NewGlobalElementModalActions';
import RealTimeActions from 'flux/actions/RealTimeActions';
import draftJsUtils from '@shoflo-rundown/draft-js-utils';
import { isGlobalElementManager } from 'main/components/show/cuesheet/cuesheetViewHelper';
import UserSessionStore from 'flux/stores/UserSessionStore';
import {
  applyRowMoveDelta,
  remoteRowOrderDeferred,
} from 'flux/stores/globalElementRowOrderSync';
import role from 'shared/lib/role';

/**
 *  Global Element Store
 */
export default Reflux.createStore({
  listenables: [GlobalElementActions],

  mixins: [BaseMixin],

  init() {
    this.setState(this._getInitState());

    this.listenTo(
      GlobalElementSetEditFormActions.save.completed,
      'onSaveGlobalElementSetCompleted',
    );
    this.listenTo(
      GlobalElementSetFormActions.create.completed,
      'onCreateGlobalElementSetCompleted',
    );
    this.listenTo(
      GlobalElementSetFormActions.delete,
      'onDeleteGlobalElementSet',
    );
    this.listenTo(
      RealTimeActions.itemRowsAddedRealTime,
      'onItemRowsAddedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowMutatedRealTime,
      'onItemRowMutatedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowMultipleCellsMutatedRealTime,
      'onItemRowMultipleCellsMutatedRealTime',
    );
    this.listenTo(RealTimeActions.rowUpdatedRealTime, 'onRowUpdateRealTime');
    this.listenTo(
      RealTimeActions.createdGlobalElementFromShowRowRealTime,
      'onCreatedGlobalElementFromShowRowRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowsMovedRealTime,
      'onItemRowsMovedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowsDeletedRealTime,
      'onItemRowsDeletedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowPresentationUpdatedRealTime,
      'onItemRowPresentationUpdatedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowOrderUpdatedRealTime,
      'onItemRowOrderUpdatedRealTime',
    );
    this.listenTo(
      RealTimeActions.publishShowRowChangesToGlobalElementRowRealTime,
      'onPublishShowRowChangesToGlobalElementRowRealTime',
    );
    this.listenTo(
      CuesheetRowActions.publishShowRowChangesToGlobalElementRowPersistent
        .completed,
      'onPublishShowRowChangesToGlobalElementRowPersistentCompleted',
    );

    this.listenTo(
      NewGlobalElementModalActions.createGlobalElementFromShowRow.completed,
      'onCreateGlobalElementFromShowRowCompleted',
    );
    this.listenTo(TagFormActions.create.completed, 'onTagsUpdated');
    this.listenTo(TagListActions.softDelete.completed, 'onTagsUpdated');
    this.listenTo(TagEditFormActions.update.completed, 'onTagsUpdated');
    this.listenTo(
      EventViewActions.populateGlobalElementManager.completed,
      'onPopulateGlobalElementManagerCompleted',
    );
    this.listenTo(
      GuestPassActions.toggleGuestPass.completed,
      'onToggleElementSetGuestPassCompleted',
    );
    this.listenTo(
      EventActions.updateField.completed,
      'onUpdateEventFieldCompleted',
    );
    this.listenTo(
      EventViewActions.populateGlobalElementManager.failed,
      'onPopulateGlobalElementManagerFailed',
    );
    this.listenTo(
      TeamViewActions.populate.completed,
      'onTeamPopulateCompleted',
    );

    this.listenTo(
      CuesheetRowActions.updateItemRowPersistent,
      'onUpdateItemRowPersistent',
    );
    this.listenTo(CuesheetRowActions.updateRow, 'onUpdateRow');
    this.listenTo(
      CuesheetRowActions.deleteItemRowsPersistent,
      'onDeleteItemRowsPersistent',
    );
    this.listenTo(
      CuesheetRowActions.addItemRowsPersistent.completed,
      'onAddItemRowsPersistentCompleted',
    );
    this.listenTo(
      CuesheetRowActions.convertToTitleRowPersistent,
      'onConvertToTitleRowPersistent',
    );
    this.listenTo(
      CuesheetRowActions.convertToStandardRowPersistent,
      'onConvertToStandardRowPersistent',
    );
    this.listenTo(CuesheetRowActions.rowsMovedLocally, 'onRowsMovedLocally');
    this.listenTo(CuesheetRowActions.rowOrderResynced, 'onRowOrderResynced');
    this.listenTo(
      CuesheetCellActions.saveMultipleDataCellChangesPersistent,
      'onSaveMultipleDataCellChangesPersistent',
    );

    this.listenTo(CuesheetActions.populate, 'onCuesheetPopulate');
    this.listenTo(
      CuesheetActions.populate.completed,
      'onCuesheetPopulateCompleted',
    );

    this.listenTo(UserSessionStore, 'onUserSessionStoreChange');
  },

  _getInitState() {
    const next = Map({
      elementSets: OrderedMap(),
      activeElementSet: Map(),
      activeEvents: OrderedMap(),
      tagRowCounts: OrderedMap(),
      tags: OrderedMap(),
      rows: OrderedMap(),
      rowsSearchTerm: '',
      rowsSearched: OrderedMap(),
    });

    return next;
  },

  onUserSessionStoreChange(state) {
    this.setInState('user', Map(state));
  },

  onTeamPopulateCompleted(team) {
    this.setInState('team', Map(team));

    if (!this.getInState('teamId') || this.getInState('teamId') !== team._id) {
      if (this._isTeamAdmin(team._id)) GlobalElementActions.populate(team._id);
    }
  },

  _isTeamAdmin(teamId) {
    if (!this.getInState('user')) return;

    return role.hasTeamAdminRole(this.getInState('user'), teamId);
  },

  onPopulate(teamId) {
    if (!teamId) return;

    const next = this.getState().set('teamId', teamId).set('isLoading', true);

    this.setStateAndTrigger(next);

    EventViewActions.populateGlobalElementManager(teamId);
  },

  onToggleElementSetGuestPassCompleted(show) {
    this.setInStateAndTrigger(['elementSets', show._id], fromJS(show));
  },

  onUpdateEventFieldCompleted(event) {
    this.setInStateAndTrigger('event', fromJS(event));
  },

  onRowsSearch(searchTerm) {
    let next = this.getState();

    next = next.set('rowsSearchTerm', searchTerm);
    if (searchTerm.length < 1) return GlobalElementActions.rowsClearSearch();

    const rows = next.get('rows').toArray();
    const searchRegex = new RegExp(searchTerm, 'ig');

    const itemMatches = new OrderedMap().withMutations((matches) => {
      const filteredRows = rows.filter((row) => {
        const title = row.getIn(['item', 'title']);
        return title ? title.match(searchRegex) : false;
      });

      filteredRows.forEach((row) => {
        matches.set(row.get('_id'), row);
      });
    });

    if (itemMatches.size > 0) {
      next = next.set('rowsSearched', itemMatches);
    } else {
      next = next.set('rowsSearched', null);
    }

    this.setStateAndTrigger(next);
  },

  onRowsClearSearch() {
    let next = this.getState();

    next = next.set('rowsSearchTerm', '').set('rowsSearched', null);

    this.setStateAndTrigger(next);
  },

  onActivateForTeam() {
    this.setInStateAndTrigger('isLoading', true);
  },

  onActivateForTeamCompleted(team) {
    EventViewActions.populateGlobalElementManager(team._id);
  },

  // eslint-disable-next-line no-unused-vars
  onActivateForTeamFailed(team) {
    this.setInStateAndTrigger('isLoading', false);
  },

  /**
   * Global element manager event for team
   */
  onPopulateGlobalElementManagerCompleted(event) {
    let next = this.getState()
      .set('event', fromJS(event))
      .set('eventId', event._id);

    RealTimeActions.subscribeToEvent(event._id);

    this.setStateAndTrigger(next);

    const teamId = this.getInState('teamId');

    GlobalElementActions.populateGlobalElementSets(teamId, event._id);
    GlobalElementActions.populateGlobalElementTagCounts(teamId, event._id);
    GlobalElementActions.populateGlobalElementTags(teamId, event._id);
  },

  onPopulateGlobalElementManagerFailed() {
    this.setInStateAndTrigger('isLoading', false);
  },

  onCreateGlobalElementSetCompleted(elementSet) {
    this.setInStateAndTrigger(
      ['elementSets', elementSet._id],
      fromJS(elementSet),
    );
  },

  onSaveGlobalElementSetCompleted(elementSet) {
    this.setInStateAndTrigger(
      ['elementSets', elementSet._id, 'title'],
      elementSet.title,
    );
  },

  onDeleteGlobalElementSet(teamId, eventId, elementSetId) {
    this.deleteInStateAndTrigger(['elementSets', elementSetId]);
  },

  onPopulateGlobalElementTagCountsCompleted(tagRowCounts) {
    this.setInStateAndTrigger('tagRowCounts', fromJS(tagRowCounts));
  },

  /**
   * Populate elementSets (shows) for the teams global element manager event
   *
   * @param elementSets (actually are shows)
   */
  onPopulateGlobalElementSetsCompleted(elementSets) {
    let next = this.getState();

    next = this._categoriesMigrationRequired(elementSets, next);

    const teamId = next.get('teamId');
    const eventId = this.getInState(['event', '_id']);

    const allElementSets = OrderedMap(
      elementSets.map((elementSet) => {
        return [elementSet._id, Map(elementSet)];
      }),
    );

    next = next.set('elementSets', allElementSets);

    next = this._setLoadingState(next);

    this.setStateAndTrigger(next);

    const activeElementSet = next.get('activeElementSet');

    //If there isn't an active element set, populate the first element set so the user doesn't have to choose something
    if (
      !activeElementSet ||
      allElementSets.keySeq().indexOf(activeElementSet.get('_id')) === -1
    ) {
      GlobalElementActions.populateElementSet(
        teamId,
        eventId,
        allElementSets.first().get('_id'),
      );
    }
  },

  onPopulateElementSet(teamId, eventId, elementSetId) {
    const activeElementSet = this.getInState(['elementSets', elementSetId]);

    const nextState = this.getState()
      .set('activeElementSet', activeElementSet)
      .set('showId', elementSetId)
      .set('rows', OrderedMap())
      .set('rowsSearchTerm', '')
      .set('rowsSearched', OrderedMap());

    this.setStateAndTrigger(nextState);
  },

  /**
   * Populate itemSetList data for a particular elementSet (show)
   *
   * @param elementSet (itemSetList)
   */
  onPopulateElementSetCompleted(elementSet) {
    const itemSet = elementSet.itemSets[0];
    const { rows } = itemSet;

    let next = this.getState()
      .set('itemSetListId', elementSet._id)
      .set('itemSetId', itemSet._id)
      .set('itemSet', itemSet);

    next = this._setPopulatedGlobalElementRows(
      itemSet.rows,
      itemSet.rowOrder,
      next,
    );
    next = this._setGlobalElementsInStateByTag(rows, next);

    this.setStateAndTrigger(next);
  },

  //On the first load, the GEM show and GEM insert modal are populated with the same show data
  onCuesheetPopulate(teamId, eventId, showId) {
    if (isGlobalElementManager() && !this.getState().get('showId')) {
      this.setInState('showId', showId);
    }
  },

  //On the first load, the GEM show and GEM insert modal are populated with the same data
  //for subsequent changes to the GEM Insert Modal, use populateElementSet
  onCuesheetPopulateCompleted(result) {
    let next = this.getState();

    if (isGlobalElementManager() && next.get('rows').size === 0) {
      const { show, itemSetList } = result;

      const itemSet = itemSetList.itemSets[0];
      const { rows } = itemSet;

      if (
        !next.get('activeElementSet') ||
        next.get('activeElementSet').size === 0
      ) {
        next = next.set('activeElementSet', Map(show));
      }

      next = next
        .set('itemSetListId', itemSetList._id)
        .set('itemSetId', itemSet._id)
        .set('itemSet', itemSet);

      next = this._setPopulatedGlobalElementRows(
        itemSet.rows,
        itemSet.rowOrder,
        next,
      );
      next = this._setGlobalElementsInStateByTag(rows, next);

      this.setStateAndTrigger(next);
    }
  },

  _categoriesMigrationRequired(elementSets, next) {
    const showWithCategories = elementSets.find((elementSet) => {
      return elementSet.categories && elementSet.categories.length > 0;
    });

    if (showWithCategories) {
      next = next.set('categoriesMigrationRequired', true);
    }

    return next;
  },

  onMigrateAllEventShowCategoriesToEventGlobalElementLabels() {
    this.setInStateAndTrigger('categoriesMigrationInProgress', true);
  },

  onMigrateAllEventShowCategoriesToEventGlobalElementLabelsCompleted(
    elementSets,
  ) {
    let next = this.getState(),
      teamId = next.get('teamId'),
      eventId = next.get('eventId'),
      currentElementSets = next.get('elementSets'),
      activeElementSet = next.get('activeElementSet');

    const allElementSets = OrderedMap(
      elementSets.map((elementSet) => {
        const rowCount = currentElementSets.getIn([elementSet._id, 'rowCount']);

        const elementSetMap = Map(elementSet).set('rowCount', rowCount);

        return [elementSet._id, elementSetMap];
      }),
    );

    next = next
      .set('elementSets', allElementSets)
      .set('categoriesMigrationInProgress', false)
      .set('categoriesMigrationRequired', false);

    if (activeElementSet && activeElementSet.get('_id')) {
      next = next.set(
        'activeElementSet',
        allElementSets.get(activeElementSet.get('_id')),
      );
      GlobalElementActions.populateElementSet(
        teamId,
        eventId,
        activeElementSet.get('_id'),
      );
    }

    this.setStateAndTrigger(next);

    GlobalElementActions.populateGlobalElementTags(teamId, eventId);
    GlobalElementActions.populateGlobalElementTagCounts(teamId, eventId);

    if (isGlobalElementManager()) {
      CuesheetActions.populate(teamId, eventId, activeElementSet.get('_id'));
    }
  },

  onTagsUpdated(event) {
    this._setTagsInStateAndTrigger(event.globalElementLabels);
  },

  onPopulateGlobalElementTagsCompleted(tags) {
    this._setTagsInStateAndTrigger(tags);
  },

  _setTagsInStateAndTrigger(tags) {
    let allTags = OrderedMap();

    tags.forEach((tag) => {
      if (tag.deleted) return false;
      allTags = allTags.set(tag._id, fromJS(tag));
    });

    let nextState = this.getState().set('tags', allTags);
    nextState = this._setLoadingState(nextState);

    this.setStateAndTrigger(nextState);
  },

  _setGlobalElementsInStateByTag(rows, state) {
    let nextState = state || this.getState();

    const organizedRows = OrderedMap().withMutations((map) => {
      rows.forEach((row) => {
        if (!map.get(row.label)) map.set(row.label, List());
        map.update(row.label, (rows) => rows.push(row));
      });

      return map;
    });

    nextState = nextState.set('globalElementsByTag', organizedRows);

    return nextState;
  },

  _setPopulatedGlobalElementRows(rows, rowOrder, state) {
    let next = state || this.getState();

    let rowLookup = {};
    rows.forEach((row) => (rowLookup[row._id] = row));

    let rowItems = OrderedMap();

    rowOrder.forEach((rowId) => {
      const row = rowLookup[rowId];

      if (!row) return;

      rowItems = rowItems.set(
        rowId,
        Map({
          _id: rowId,
          item: fromJS(row.item[0]),
          labels: row.labels,
          titleRow: row.titleRow,
        }),
      );
    });

    next = next.set('rows', rowItems);
    next = this._setLoadingState(next);

    return next;
  },

  _setLoadingState(state) {
    let next = state || this.getState();

    const elementSets = next.get('elementSets'),
      tags = next.get('tags');
    //rows = next.get('rows');

    let isLoading = false;

    if (!tags || tags.size < 1) isLoading = true;
    if (!elementSets || elementSets.size < 1) isLoading = true;

    next = next.set('isLoading', isLoading);

    return next;
  },

  // eslint-disable-next-line no-unused-vars
  onItemRowPresentationUpdatedRealTime(showId, payload, responsePayload) {
    if (showId !== this.getInState('showId')) return;

    let next = this.getState();
    let { rowId, changes } = payload;

    next = next.setIn(['rows', rowId, 'titleRow'], changes.titleRow);

    next = next.update('itemSet', (itemSet) => {
      const rowIndex = itemSet.rows.findIndex((row) => row._id === rowId);

      if (itemSet.rows[rowIndex]) {
        itemSet.rows[rowIndex].titleRow = changes.titleRow;
      }

      return itemSet;
    });

    this.setStateAndTrigger(next);
  },

  onConvertToStandardRowPersistent(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    rowId,
  ) {
    if (showId !== this.getInState('showId')) return;
    this.setInStateAndTrigger(['rows', rowId, 'titleRow'], false);
  },

  onConvertToTitleRowPersistent(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    rowId,
  ) {
    if (showId !== this.getInState('showId')) return;
    this.setInStateAndTrigger(['rows', rowId, 'titleRow'], true);
  },

  onItemRowsAddedRealTime(showId, payload, responsePayload) {
    if (showId !== this.getInState('showId')) return;

    const { itemSetId, afterRowId, rows } = responsePayload;
    this._addItemRows(itemSetId, afterRowId, rows);
  },

  onAddItemRowsPersistentCompleted(result) {
    const { itemSetId, afterRowId, rows } = result;

    /**
     * Prevent rows being added to a different show from showing up in the
     * current show that is populated in this store.
     */
    if (itemSetId !== this.getInState('itemSetId')) return;

    this._addItemRows(itemSetId, afterRowId, rows);
  },

  _addItemRows(itemSetId, afterRowId, rows) {
    let next = this.getState();

    const itemSet = next.get('itemSet');

    /*
     * No itemSet has been populated because a team hasn't had the global
     * element functionality enabled. So if there are any
     * actions that come through then don't do anything with those actions
     */
    if (!itemSet) return;

    const indexOfAfterRowId = itemSet.rowOrder.indexOf(afterRowId) + 1;

    const newRowIds = rows.map((r) => r._id).reverse();

    newRowIds.forEach((rowId) => {
      itemSet.rowOrder.splice(indexOfAfterRowId, 0, rowId);
    });

    itemSet.rows = itemSet.rows.concat(rows);

    next = next.set('itemSet', itemSet);

    next = this._setPopulatedGlobalElementRows(
      itemSet.rows,
      itemSet.rowOrder,
      next,
    );

    this.setStateAndTrigger(next);
  },

  onDeleteItemRowsPersistent(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    rowIds,
  ) {
    if (this.getInState('showId') !== showId) return;
    this._deleteItemRows(rowIds);
  },

  // eslint-disable-next-line no-unused-vars
  onItemRowsDeletedRealTime(showId, { itemSetId, rowIds }) {
    if (this.getInState('showId') !== showId) return;
    this._deleteItemRows(rowIds);
  },

  _deleteItemRows(rowIds) {
    let next = this.getState(),
      itemSet = next.get('itemSet');

    if (!itemSet) return;

    rowIds.forEach((rowId) => {
      let rowIdIndex = itemSet.rowOrder.indexOf(rowId);
      itemSet.rowOrder.splice(rowIdIndex, 1);
    });

    next = next.set('itemSet', itemSet);

    next = this._setPopulatedGlobalElementRows(
      itemSet.rows,
      itemSet.rowOrder,
      next,
    );

    this.setStateAndTrigger(next);
  },

  onPublishShowRowChangesToGlobalElementRowRealTime(
    showId,
    payload,
    responsePayload, // eslint-disable-line no-unused-vars
  ) {
    if (showId !== this.getInState('showId')) return;
    this._applyGlobalElementRowUpdate(payload);
  },

  onPublishShowRowChangesToGlobalElementRowPersistentCompleted(geRow) {
    this._applyGlobalElementRowUpdate(geRow);
  },

  _applyGlobalElementRowUpdate(geRow) {
    if (!geRow || !this.getInState(['rows', geRow._id])) return;

    const next = this.getState()
      .update('itemSet', (itemSet) => {
        const rowIndex = itemSet.rows.findIndex((row) => row._id === geRow._id);

        if (itemSet.rows[rowIndex]) {
          itemSet.rows[rowIndex] = geRow;
        }

        return itemSet;
      })
      .updateIn(['rows', geRow._id], (row) => {
        return row
          .set('item', fromJS(geRow.item[0]))
          .set('titleRow', geRow.titleRow);
      });

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onItemRowMutatedRealTime(showId, payload, responsePayload) {
    //If changes are made to a show outside of the global element manager
    //then do nothing. Only update the list for changes inside global element
    //manager
    if (showId !== this.getInState('showId')) return;

    let { rowId, changes } = payload;
    this._updateItemRow(rowId, changes);
  },

  // eslint-disable-next-line no-unused-vars
  onItemRowMultipleCellsMutatedRealTime(showId, payload, responsePayload) {
    if (showId !== this.getInState('showId')) return;

    const { changes } = payload;

    Object.keys(changes).forEach((rowId) => {
      this._updateItemRow(rowId, changes[rowId]);
    });
  },

  onUpdateItemRowPersistent(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    rowId,
    changes,
  ) {
    //If changes are made to a show outside of the global element manager
    //then do nothing. Only update the list for changes inside global element
    //manager
    if (showId !== this.getInState('showId')) return;

    this._updateItemRow(rowId, changes);
  },

  _updateItemRow(rowId, changes) {
    let next = this.getState();
    let updateData = {};

    if (changes.ItemTitle)
      updateData.rteTitleState = changes.ItemTitle.rteState;
    if (changes.rteTitleState) updateData.rteTitleState = changes.rteTitleState;
    if (changes.durationInSeconds)
      updateData.durationInSeconds = changes.durationInSeconds;

    if (updateData.rteTitleState) {
      updateData.title = draftJsUtils.getPlainTextFromRawRTEState(
        updateData.rteTitleState,
      );
    }

    next = next.update('itemSet', (itemSet) => {
      const rowIndex = itemSet.rows.findIndex((row) => row._id === rowId);

      if (itemSet.rows[rowIndex]) {
        itemSet.rows[rowIndex].item[0] = Object.assign(
          {},
          itemSet.rows[rowIndex].item[0],
          updateData,
        );
      }

      return itemSet;
    });

    next = next.updateIn(['rows', rowId, 'item'], (item) => {
      Object.keys(updateData).forEach((key) => {
        item = item.set(key, updateData[key]);
      });

      return item;
    });

    this.setStateAndTrigger(next);
  },

  onCreatedGlobalElementFromShowRowRealTime(params, payload, responsePayload) {
    if (params.showId !== this.getInState('showId')) return;

    responsePayload.forEach((row) => {
      this._addNewRow(row);
    });
  },

  onCreateGlobalElementFromShowRowCompleted(rows) {
    if (!this.getInState('itemSet')) return;

    rows.forEach((row) => {
      this._addNewRow(row);
    });
  },

  _addNewRow(row) {
    let next = this.getState();

    const itemSet = next.get('itemSet');

    itemSet.rowOrder.push(row._id);
    itemSet.rows.push(row);

    next = next.set('itemSet', itemSet);

    next = this._setPopulatedGlobalElementRows(
      itemSet.rows,
      itemSet.rowOrder,
      next,
    );

    this.setStateAndTrigger(next);
  },

  onRowUpdateRealTime(showId, payload, responsePayload) {
    if (showId !== this.getInState('showId')) return;

    let next = this.getState();

    const row = responsePayload;

    let itemSet = this.getInState('itemSet');

    const indexOfItemSetRow = itemSet.rows.findIndex((itemSetRow) => {
      return itemSetRow._id === row._id;
    });

    itemSet.rows[indexOfItemSetRow] = row;

    next = next.set('itemSet', itemSet);
    next = next.setIn(
      ['rows', row._id],
      Map({
        _id: row._id,
        item: fromJS(row.item[0]),
        labels: row.labels,
      }),
    );

    this.setStateAndTrigger(next);
  },

  onUpdateRow(rowIndex, changes) {
    let next = this.getState();

    const itemSet = next.get('itemSet');
    const rowId = itemSet.rowOrder[rowIndex];

    next = next.updateIn(['rows', rowId], (row) => {
      return row.merge(changes);
    });

    this.setStateAndTrigger(next);
  },

  onSaveMultipleDataCellChangesPersistent(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    changes,
  ) {
    //If changes are made to a show outside of the global element manager
    //then do nothing. Only update the list for changes inside global element
    //manager
    if (showId !== this.getInState('showId')) return;

    let next = this.getState();

    Object.keys(changes).forEach((rowId) => {
      let itemChanges = changes[rowId].ItemTitle;

      if (!itemChanges || !itemChanges.rteTitleState) return;

      next = next.updateIn(['rows', rowId, 'item'], (item) => {
        return item
          .set('rteTitleState', itemChanges.rteTitleState)
          .set(
            'title',
            draftJsUtils.getPlainTextFromRawRTEState(itemChanges.rteTitleState),
          );
      });
    });

    this.setStateAndTrigger(next);
  },

  onItemRowOrderUpdatedRealTime(showId, { itemSetId }, newRowOrder) {
    if (this.getInState('showId') !== showId) return;
    if (remoteRowOrderDeferred(itemSetId)) return;
    this._updateRowOrder(newRowOrder);
  },

  onItemRowsMovedRealTime(showId, { itemSetId }, newRowOrder) {
    if (this.getInState('showId') !== showId) return;
    if (remoteRowOrderDeferred(itemSetId)) return;
    this._updateRowOrder(newRowOrder);
  },

  onRowsMovedLocally(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    rowIds,
    afterRowId,
  ) {
    if (this.getInState('showId') !== showId) return;

    const itemSet = this.getState().get('itemSet');

    //Only apply the delta to the itemSet it belongs to: during populate and
    //element set switches the store can briefly hold no itemSet, or the
    //previous one
    if (!itemSet || itemSet._id !== itemSetId) return;

    if (!applyRowMoveDelta(itemSet.rowOrder, rowIds, afterRowId)) return;

    this._updateRowOrder(itemSet.rowOrder);
  },

  /**
   * Apply the authoritative order the cuesheet re-synced to after its moveRows
   * request settled. We only saw the optimistic delta via onRowsMovedLocally
   * and never receive the HTTP response, so this is how a concurrent remote
   * move (merged into the response) reaches us.
   */
  onRowOrderResynced(showId, itemSetId, rowOrder) {
    if (this.getInState('showId') !== showId) return;

    const itemSet = this.getState().get('itemSet');
    //Only the itemSet this order belongs to — the GEM may be showing another
    if (!itemSet || itemSet._id !== itemSetId) return;

    this._updateRowOrder(rowOrder);
  },

  _updateRowOrder(rowOrder) {
    let next = this.getState();

    let itemSet = next.get('itemSet');

    if (!itemSet) return;

    itemSet.rowOrder = rowOrder;

    next = next.set('itemSet', itemSet);

    next = this._setPopulatedGlobalElementRows(
      itemSet.rows,
      itemSet.rowOrder,
      next,
    );

    this.setStateAndTrigger(next);
  },
});
